import { localPoint } from '@visx/event';

export const X_AXIS_FONT_SIZE = 12;
export const X_AXIS_GAP = 4;
export const X_AXIS_HEIGHT = X_AXIS_FONT_SIZE + X_AXIS_GAP;
export const X_AXIS_TICK_SPACING = 16;
export const TOOLTIP_FONT_SIZE = 12;
export const TOOLTIP_PADDING = 8;
export const TOOLTIP_ITEM_SPACING = 8;
export const TOOLTIP_RESERVED_HEIGHT =
  4 * TOOLTIP_FONT_SIZE + TOOLTIP_ITEM_SPACING * 3 + TOOLTIP_PADDING * 2;
export const MARKERS_OFFSET = 24;
export const MARKER_LINE_WIDTH = 2;
export const MARKER_FONT_SIZE = 12;
export const MARKER_PADDING = 4;
export const MARKER_HEIGHT = MARKER_FONT_SIZE + MARKER_PADDING * 2;
export const MARKERS_HEIGHT = 2 * MARKERS_OFFSET + MARKER_HEIGHT;

export const MARGIN_BOTTOM = MARKERS_HEIGHT;

export type MarkerType = 'SHARE_PRICE' | 'MY_FAIR_VALUE';

export const MARKER_COLORS_CSS_VARS: Record<MarkerType, string> = {
  SHARE_PRICE: 'var(--s-color-brand-01)',
  MY_FAIR_VALUE: 'var(--s-color-chart-03)',
};

/**
 * Improved fallback measurement specifically designed for headless environments
 * Uses character-specific width mapping for better accuracy
 */
const fallbackMeasureWidth = (text: string): number => {
  const CHAR_WIDTH_MAP = {
    '0': 6.744140625,
    '1': 6.744140625,
    '2': 6.744140625,
    '3': 6.744140625,
    '4': 6.744140625,
    '5': 6.744140625,
    '6': 6.744140625,
    '7': 6.744140625,
    '8': 6.744140625,
    '9': 6.744140625,
    a: 6.52734375,
    b: 6.73828125,
    c: 6.28125,
    d: 6.767578125,
    e: 6.36328125,
    f: 4.171875,
    g: 6.73828125,
    h: 6.609375,
    i: 2.91796875,
    j: 2.87109375,
    k: 6.08203125,
    l: 2.91796875,
    m: 10.5234375,
    n: 6.626953125,
    o: 6.84375,
    p: 6.73828125,
    q: 6.8203125,
    r: 4.06640625,
    s: 6.193359375,
    t: 3.92578125,
    u: 6.615234375,
    v: 5.8125,
    w: 9.017578125,
    x: 5.953125,
    y: 5.677734375,
    z: 5.953125,
    A: 7.828125,
    B: 7.4765625,
    C: 7.810546875,
    D: 7.875,
    E: 6.8203125,
    F: 6.6328125,
    G: 8.173828125,
    H: 8.560546875,
    I: 3.263671875,
    J: 6.62109375,
    K: 7.529296875,
    L: 6.462890625,
    M: 10.4765625,
    N: 8.560546875,
    O: 8.255859375,
    P: 7.5703125,
    Q: 8.255859375,
    R: 7.39453125,
    S: 7.125,
    T: 7.16015625,
    U: 7.78125,
    V: 7.640625,
    W: 10.646484375,
    X: 7.5234375,
    Y: 7.20703125,
    Z: 7.189453125,
    ' ': 2.9765625,
    '.': 3.1640625,
    ',': 2.361328125,
    ';': 2.537109375,
    ':': 2.90625,
    '-': 3.31640625,
    _: 5.4140625,
    $: 6.744140625,
    '%': 8.7890625,
    '&': 7.46484375,
    '(': 4.107421875,
    ')': 4.177734375,
    '[': 3.181640625,
    ']': 3.181640625,
    '{': 4.060546875,
    '}': 4.060546875,
    '+': 6.80859375,
    '=': 6.5859375,
    '/': 4.951171875,
    '\\': 4.927734375,
    '@': 10.775390625,
    '#': 7.388671875,
    '*': 5.16796875,
    '"': 3.84375,
    '`': 3.708984375,
    "'": 2.09765625,
    '<': 6.099609375,
    '>': 6.275390625,
    '|': 2.9296875,
  };

  let totalWidth = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charWidth = CHAR_WIDTH_MAP[char] || MARKER_FONT_SIZE * 0.6;
    totalWidth += charWidth;
  }
  return totalWidth;
};

/**
 * Measure the width of a text string using a canvas context.
 * This ensures cross-browser compatibility where some browsers might
 * load fonts asynchronously.
 * @param text - The text to measure.
 * @returns The width of the text in pixels.
 */
export const measureTextWidth = (text: string, fontSize: number) => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;

    context.font = `${fontSize}px Roboto, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif`;
    return context.measureText(text).width;
  } catch (e) {
    return fallbackMeasureWidth(text);
  }
};

export const getMarkerBoxWidth = (label: string, value: string) => {
  const labelWidth = measureTextWidth(label, MARKER_FONT_SIZE);
  const valueWidth = measureTextWidth(value, MARKER_FONT_SIZE);
  const spaceWidth = measureTextWidth('-', MARKER_FONT_SIZE);
  const textWidth = labelWidth + valueWidth + spaceWidth;
  const startPadding = MARKER_LINE_WIDTH + MARKER_PADDING;
  const boxWidth = startPadding + MARKER_PADDING + textWidth;

  return {
    boxWidth,
    labelWidth,
    spaceWidth,
    startPadding,
  };
};

type Box = {
  x: number;
  width: number;
};

/**
 * Detects how much two boxes overlap based on their x-coordinate and width.
 * @param box1 - The first box with x and width properties.
 * @param box2 - The second box with x and width properties.
 * @returns The amount of overlap in pixels.
 */
export const measureBoxOverlap = (box1: Box, box2: Box): number => {
  const box1End = box1.x + box1.width;
  const box2End = box2.x + box2.width;
  // If no overlap, return 0
  if (box1.x >= box2End || box2.x >= box1End) {
    return 0;
  }

  // Calculate overlap amount by finding the minimum end point minus the maximum start point
  return Math.min(box1End, box2End) - Math.max(box1.x, box2.x);
};

export const getXPosition = (
  e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>,
  rect: DOMRect
) => {
  const point = localPoint(e);

  if (point?.x) return point.x;

  if ('touches' in e && e.touches.length > 0) {
    return e.touches[0].clientX - rect.x;
  }

  return 0;
};
